import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { useForgotPassword } from "../../hooks/useForgotPassword";
import {
  createForgotPasswordSchema,
  ForgotPasswordFormData,
} from "../../helpers/userValidation";

const ForgotPasswordForm: React.FC = () => {
  const { t } = useTranslation();
  const { handleForgotPassword, isLoading, isSuccess } = useForgotPassword();

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(createForgotPasswordSchema(t)),
  });

  // --- TIMESTAMP LOGIC ---
  useEffect(() => {
    register("form_load_timestamp");
    setValue("form_load_timestamp", Math.floor(Date.now() / 1000));
  }, [register, setValue]);
  // --- END TIMESTAMP ---

  const onSubmit = async (data: ForgotPasswordFormData) => {
    await handleForgotPassword(data);
  };

  if (isSuccess) {
    return (
      <div className="auth-success-message">
        <h3>{t("user.forgotPassword.checkEmailTitle", "Check your email")}</h3>
        <p>
          {t(
            "user.forgotPassword.checkEmailMessage",
            "We have sent a password reset link to your email address."
          )}
        </p>
        <a
          href={window.commonVariables?.loginUrl}
          className="button button--secondary"
        >
          {t("user.auth.backToLogin", "Back to Login")}
        </a>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="auth-form">
      <h2>{t("user.forgotPassword.title", "Forgot Password")}</h2>
      <p className="auth-form__description">
        {t(
          "user.forgotPassword.description",
          "Enter your email address and we'll send you a link to reset your password."
        )}
      </p>

      {/* --- HONEYPOT FIELD --- */}
      <div className="honeypot-field" aria-hidden="true">
        <label htmlFor="fp_nickname">Nickname</label>
        <input
          id="fp_nickname"
          type="text"
          {...register("nickname")}
          tabIndex={-1}
          autoComplete="off"
        />
      </div>
      {/* --- END HONEYPOT --- */}

      <div className="form-field">
        <label htmlFor="fp-email">{t("user.auth.emailLabel")}</label>
        <input
          id="fp-email"
          type="email"
          {...register("email")}
          className={errors.email ? "has-error" : ""}
        />
        {errors.email && (
          <p className="form-field__error">{errors.email.message}</p>
        )}
      </div>

      <button type="submit" disabled={isLoading}>
        {isLoading
          ? t("user.forgotPassword.sending", "Sending...")
          : t("user.forgotPassword.submit", "Reset Password")}
      </button>

      <div className="auth-form__footer">
        <a href={window.commonVariables?.loginUrl} className="auth-link">
          {t("user.auth.backToLogin", "Back to Login")}
        </a>
      </div>
    </form>
  );
};

export default ForgotPasswordForm;
